import { Injectable, inject, signal } from "@angular/core"
import { environment } from "../../../environments/environment"
import { AuthService } from "../../core/services/auth.service"
import { ClerkService } from "../../core/services/clerk.service"
import { ChatContextService } from "./chat-context.service"

export interface ChatMessage {
  id: string
  role: "user" | "assistant"
  content: string
  isStreaming?: boolean
}

export interface ChatSession {
  id: string
  name: string | null
  created_at: string
  updated_at: string | null
  message_count: number
}

@Injectable({ providedIn: "root" })
export class ChatService {
  private auth = inject(AuthService)
  private clerkService = inject(ClerkService)
  private context = inject(ChatContextService)

  readonly messages = signal<ChatMessage[]>([])
  readonly isStreaming = signal(false)
  readonly sessionId = signal<string | null>(null)
  readonly error = signal<string | null>(null)
  readonly sessions = signal<ChatSession[]>([])

  async sendMessage(userContent: string): Promise<void> {
    const user = this.auth.currentUserValue
    if (!user?.account_id) return

    this.error.set(null)

    // Add user message immediately
    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: userContent,
    }
    this.messages.update((msgs) => [...msgs, userMsg])

    // Add empty assistant placeholder
    const assistantId = crypto.randomUUID()
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
      isStreaming: true,
    }
    this.messages.update((msgs) => [...msgs, assistantMsg])
    this.isStreaming.set(true)

    const ctx = this.context.context()
    const url = `${environment.apiUrl}/accounts/${user.account_id}/chat/message`

    try {
      const token = await this.clerkService.getToken()
      if (!token) {
        throw new Error("Not authenticated — please refresh and try again.")
      }
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: userContent,
          session_id: this.sessionId(),
          entity_id: ctx.entityId,
          page_context: {
            route: ctx.route,
            entity_id: ctx.entityId,
            description: ctx.description,
          },
        }),
      })

      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`)
      }

      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ""

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n")
        buffer = lines.pop() ?? ""

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue
          const jsonStr = line.slice(6).trim()
          if (!jsonStr) continue

          try {
            const event = JSON.parse(jsonStr)
            this.handleEvent(event, assistantId)
          } catch {
            // Ignore malformed SSE lines
          }
        }

        // Yield to browser event loop so Angular can render each chunk
        await new Promise<void>((resolve) => setTimeout(resolve, 0))
      }
    } catch (err) {
      this.error.set(`Failed to send message: ${err}`)
      this.messages.update((msgs) =>
        msgs.map((m) =>
          m.id === assistantId
            ? { ...m, content: "⚠️ Error: could not reach the AI advisor.", isStreaming: false }
            : m,
        ),
      )
    } finally {
      this.isStreaming.set(false)
      this.messages.update((msgs) =>
        msgs.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m)),
      )
    }
  }

  private handleEvent(event: Record<string, unknown>, assistantId: string): void {
    switch (event["type"]) {
      case "session_id":
        this.sessionId.set(event["session_id"] as string)
        break
      case "token":
        this.messages.update((msgs) =>
          msgs.map((m) =>
            m.id === assistantId ? { ...m, content: m.content + (event["content"] as string) } : m,
          ),
        )
        break
      case "session_title": {
        const title = event["title"] as string
        const sid = this.sessionId()
        if (sid) {
          this.sessions.update((list) =>
            list.map((s) => (s.id === sid ? { ...s, name: title } : s)),
          )
        }
        break
      }
      case "error":
        this.error.set(event["error"] as string)
        // Remove the empty assistant placeholder — it never received content
        this.messages.update((msgs) => msgs.filter((m) => m.id !== assistantId || m.content !== ""))
        break
    }
  }

  clearSession(): void {
    this.messages.set([])
    this.sessionId.set(null)
    this.error.set(null)
  }

  async loadSessions(): Promise<void> {
    const user = this.auth.currentUserValue
    if (!user?.account_id) return
    try {
      const token = await this.clerkService.getToken()
      if (!token) return
      const resp = await fetch(`${environment.apiUrl}/accounts/${user.account_id}/chat/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (resp.ok) {
        const data: ChatSession[] = await resp.json()
        this.sessions.set(data)
      }
    } catch {
      // ignore network errors
    }
  }

  async resumeSession(sessionId: string): Promise<void> {
    const user = this.auth.currentUserValue
    if (!user?.account_id) return
    try {
      const token = await this.clerkService.getToken()
      if (!token) return
      const resp = await fetch(
        `${environment.apiUrl}/accounts/${user.account_id}/chat/sessions/${sessionId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!resp.ok) return
      const data: Array<{ message_id: string; role: string; content: string }> = await resp.json()
      this.messages.set(
        data
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map((m) => ({
            id: m.message_id,
            role: m.role as "user" | "assistant",
            content: m.content,
          })),
      )
      this.sessionId.set(sessionId)
      this.error.set(null)
    } catch {
      // ignore network errors
    }
  }

  async deleteSession(sessionId: string): Promise<void> {
    const user = this.auth.currentUserValue
    if (!user?.account_id) return
    try {
      const token = await this.clerkService.getToken()
      if (!token) return
      await fetch(`${environment.apiUrl}/accounts/${user.account_id}/chat/sessions/${sessionId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      })
      this.sessions.update((list) => list.filter((s) => s.id !== sessionId))
      if (this.sessionId() === sessionId) {
        this.clearSession()
      }
    } catch {
      // ignore network errors
    }
  }
}
